const ACCEPTED_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
]);

const clamp = (value, min = 0, max = 255) => Math.max(min, Math.min(max, value));

/**
 * Custom Error class for document image processing and validation failures.
 *
 * @class DocumentImageError
 * @extends Error
 */
export class DocumentImageError extends Error {
  /**
   * Creates an instance of DocumentImageError.
   *
   * @param {string} message - Human readable error message.
   * @param {string} code - Error code identifier.
   * @param {Object} [diagnostics={}] - Additional diagnostic details.
   */
  constructor(message, code, diagnostics = {}) {
    super(message);
    this.name = "DocumentImageError";
    this.code = code;
    this.diagnostics = diagnostics;
  }
}

/**
 * Check if the uploaded file has a supported image MIME type.
 *
 * @function isSupportedImage
 * @param {File} file - File object to validate.
 * @returns {boolean} True if file type is accepted or empty; false otherwise.
 */
export const isSupportedImage = (file) => {
  const type = (file?.type || "").toLowerCase();
  return type ? ACCEPTED_IMAGE_TYPES.has(type) : true;
};

/**
 * Convert a File object to a raw Base64 string (excluding data URI prefix).
 *
 * @function fileToBase64
 * @param {File} file - File to convert.
 * @returns {Promise<string>} Base64 encoded string of the file.
 */
export const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const canvasToBlob = (canvas, type = "image/jpeg", quality = 0.9) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Canvas export failed"))),
      type,
      quality,
    );
  });

const loadBitmap = async (file) => {
  if (window.createImageBitmap) {
    try {
      return await window.createImageBitmap(file, { imageOrientation: "from-image" });
    } catch {
      // Fall back to HTMLImageElement decode below. Some iOS HEIC files cannot be
      // decoded by canvas; those will surface as a user-facing unsupported image.
    }
  }

  const url = URL.createObjectURL(file);
  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
};

const drawContained = (source, maxLongEdge) => {
  const width = source.width;
  const height = source.height;
  const scale = Math.min(1, maxLongEdge / Math.max(width, height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas;
};

const cornerAverage = (data, width, height) => {
  const sample = Math.max(12, Math.round(Math.min(width, height) * 0.05));
  const corners = [
    [0, 0],
    [width - sample, 0],
    [0, height - sample],
    [width - sample, height - sample],
  ];
  let total = 0;
  let count = 0;

  corners.forEach(([sx, sy]) => {
    for (let y = sy; y < sy + sample; y += 2) {
      for (let x = sx; x < sx + sample; x += 2) {
        const i = (y * width + x) * 4;
        total += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
        count += 1;
      }
    }
  });

  return count ? total / count : 128;
};

const detectDocumentBounds = (canvas) => {
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  const { width, height } = canvas;
  const { data } = ctx.getImageData(0, 0, width, height);
  const bg = cornerAverage(data, width, height);

  // Adaptive threshold: dark backgrounds need a lower threshold
  const threshold = bg < 80 ? 20 : 28;

  let minX = width;
  let minY = height;
  let maxX = 0;
  let maxY = 0;
  let hits = 0;

  for (let y = 0; y < height; y += 3) {
    for (let x = 0; x < width; x += 3) {
      const i = (y * width + x) * 4;
      const lum = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      if (Math.abs(lum - bg) > threshold) {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
        hits += 1;
      }
    }
  }

  const area = (maxX - minX) * (maxY - minY);
  const coverage = area / (width * height);

  // Accept wider coverage range for phone photos with dark surrounds
  if (!hits || coverage < 0.08 || coverage > 0.99) {
    return { x: 0, y: 0, width, height, detected: false, coverage };
  }

  // Smaller padding for tighter crop on cheques
  const pad = Math.round(Math.min(width, height) * 0.015);
  const x = Math.max(0, minX - pad);
  const y = Math.max(0, minY - pad);
  const right = Math.min(width, maxX + pad);
  const bottom = Math.min(height, maxY + pad);
  return {
    x,
    y,
    width: right - x,
    height: bottom - y,
    detected: true,
    coverage,
  };
};

const cropCanvas = (canvas, bounds) => {
  const out = document.createElement("canvas");
  out.width = bounds.width;
  out.height = bounds.height;
  const ctx = out.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(
    canvas,
    bounds.x,
    bounds.y,
    bounds.width,
    bounds.height,
    0,
    0,
    bounds.width,
    bounds.height,
  );
  return out;
};

const enhanceForOcr = (canvas) => {
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const { data } = image;
  const lumas = [];

  for (let i = 0; i < data.length; i += 4) {
    const lum = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    lumas.push(lum);
  }

  lumas.sort((a, b) => a - b);
  const low = lumas[Math.floor(lumas.length * 0.04)] || 0;
  const high = lumas[Math.floor(lumas.length * 0.96)] || 255;
  const span = Math.max(40, high - low);

  for (let i = 0; i < data.length; i += 4) {
    const lum = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    const normalized = clamp(((lum - low) / span) * 255);
    const sharpened = normalized < 145 ? normalized * 0.88 : 255 - (255 - normalized) * 0.82;
    data[i] = data[i + 1] = data[i + 2] = clamp(sharpened);
  }

  ctx.putImageData(image, 0, 0);
  return canvas;
};

const estimateBlur = (canvas) => {
  const sample = drawContained(canvas, 420);
  const ctx = sample.getContext("2d", { willReadFrequently: true });
  const { width, height } = sample;
  const { data } = ctx.getImageData(0, 0, width, height);
  let sum = 0;
  let sumSq = 0;
  let count = 0;

  const lumAt = (x, y) => {
    const i = (y * width + x) * 4;
    return 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
  };

  for (let y = 1; y < height - 1; y += 2) {
    for (let x = 1; x < width - 1; x += 2) {
      const lap =
        lumAt(x - 1, y) +
        lumAt(x + 1, y) +
        lumAt(x, y - 1) +
        lumAt(x, y + 1) -
        4 * lumAt(x, y);
      sum += lap;
      sumSq += lap * lap;
      count += 1;
    }
  }

  const mean = count ? sum / count : 0;
  return count ? sumSq / count - mean * mean : 0;
};

/**
 * Prepares a document image for OCR processing by resizing, detecting boundaries, cropping, and contrast-enhancing.
 *
 * @async
 * @function prepareDocumentImage
 * @param {File} inputFile - Raw document image file.
 * @param {Object} [options={}] - Configuration options.
 * @param {number} [options.maxLongEdge=2200] - Maximum dimension for long edge.
 * @param {number} [options.quality=0.9] - Compression quality for output JPEG.
 * @param {boolean} [options.rejectBlur=false] - Whether to throw an error if image is blurry.
 * @param {number} [options.minBlurScore=20] - Minimum acceptable blur score threshold.
 * @param {string} [options.fileName="document-scan.jpg"] - Filename for output image.
 * @returns {Promise<Object>} Processed image object containing file, base64 strings, previewUrl, and diagnostics.
 * @throws {DocumentImageError} If file is missing, unsupported, invalid, or blurry.
 */
export const prepareDocumentImage = async (
  inputFile,
  {
    maxLongEdge = 2200,
    quality = 0.9,
    rejectBlur = false,
    minBlurScore = 20,
    fileName = "document-scan.jpg",
  } = {},
) => {
  if (!inputFile) throw new DocumentImageError("No image selected", "NO_FILE");
  if (!isSupportedImage(inputFile)) {
    throw new DocumentImageError("Unsupported image type", "UNSUPPORTED_MIME", {
      type: inputFile.type,
    });
  }

  const source = await loadBitmap(inputFile).catch((error) => {
    throw new DocumentImageError("Could not decode image", "DECODE_FAILED", {
      type: inputFile.type,
      message: error?.message,
    });
  });

  const normalized = drawContained(source, maxLongEdge);
  const detectionCanvas = drawContained(normalized, 900);
  const bounds = detectDocumentBounds(detectionCanvas);
  const scaleX = normalized.width / detectionCanvas.width;
  const scaleY = normalized.height / detectionCanvas.height;
  const cropBounds = bounds.detected
    ? {
        x: Math.round(bounds.x * scaleX),
        y: Math.round(bounds.y * scaleY),
        width: Math.round(bounds.width * scaleX),
        height: Math.round(bounds.height * scaleY),
      }
    : { x: 0, y: 0, width: normalized.width, height: normalized.height };
  cropBounds.width = Math.max(1, Math.min(cropBounds.width, normalized.width - cropBounds.x));
  cropBounds.height = Math.max(1, Math.min(cropBounds.height, normalized.height - cropBounds.y));

  const cropped = cropCanvas(normalized, cropBounds);
  const blurScore = estimateBlur(cropped);
  // Only reject blur on desktop — mobile cameras (iPad/iPhone/Android) produce
  // HEIC/compressed images that score low on Laplacian variance even when sharp.
  const isMobileDevice = /Mobi|Android|iPad|iPhone|iPod/i.test(navigator.userAgent);
  if (rejectBlur && !isMobileDevice && blurScore < minBlurScore) {
    throw new DocumentImageError("The image is too blurry to scan", "BLURRY_IMAGE", {
      blurScore,
      minBlurScore,
    });
  }

  const fallbackBlob = await canvasToBlob(cropped, "image/jpeg", quality);
  const fallbackFile = new File([fallbackBlob], fileName, {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
  const fallbackBase64 = await fileToBase64(fallbackFile);

  const enhanced = enhanceForOcr(cropped);
  const blob = await canvasToBlob(enhanced, "image/jpeg", quality);
  const file = new File([blob], fileName, { type: "image/jpeg", lastModified: Date.now() });
  const base64 = await fileToBase64(file);

  return {
    file,
    base64,
    fallbackBase64,
    mimeType: "image/jpeg",
    previewUrl: URL.createObjectURL(file),
    diagnostics: {
      sourceType: inputFile.type || "unknown",
      sourceSize: inputFile.size,
      width: enhanced.width,
      height: enhanced.height,
      documentDetected: bounds.detected,
      documentCoverage: bounds.coverage,
      blurScore,
    },
  };
};

/**
 * Check if the scanned result data contains non-empty readable fields.
 *
 * @function hasReadableScanData
 * @param {Object} data - OCR output data object.
 * @returns {boolean} True if readable data is present; false otherwise.
 */
export const hasReadableScanData = (data) => {
  if (!data || typeof data !== "object") return false;
  return Object.entries(data).some(([key, value]) => {
    if (key === "confidence") return false;
    if (Array.isArray(value)) return value.length > 0;
    if (value && typeof value === "object") return hasReadableScanData(value);
    return Boolean(value);
  });
};

/**
 * Check if OCR confidence score is below acceptable threshold (< 0.55).
 *
 * @function isLowConfidence
 * @param {Object} data - OCR scan data containing confidence/score fields.
 * @returns {boolean} True if confidence is low (< 0.55); false otherwise.
 */
export const isLowConfidence = (data) => {
  const confidence = Number(data?.confidence ?? data?.ocrConfidence ?? data?.score);
  return Number.isFinite(confidence) && confidence > 0 && confidence < 0.55;
};
